import React, { useState } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import swal from "sweetalert";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import methodModel from "../../methods/methods";

// ─── Validators ───────────────────────────────────────────────────
const isValidEmail = (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim());
const isValidPhone = (val) => {
  const digits = val.replace(/\D/g, "");
  return digits.length >= 7 && digits.length <= 15;
};

// ─── Empty form ───────────────────────────────────────────────────
const emptyForm = {
  fullName: "",
  email: "",
  mobileNo: "",
  message: "",
};

// ─── FormInput ────────────────────────────────────────────────────
const FormInput = ({ label, name, type = "text", required, textarea, value, onChange, error, placeholder }) => (
  <div>
    <label className="block text-sm text-gray-700 mb-1">
      {label} {required && <span className="text-red-400">*</span>}
    </label>
    {textarea ? (
      <textarea
        name={name}
        value={value}
        onChange={onChange}
        rows={5}
        placeholder={placeholder}
        className={`w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all resize-none ${error ? "border-red-400" : "border-gray-200"
          }`}
      />
    ) : (
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all h-11 ${error ? "border-red-400" : "border-gray-200"
          }`}
      />
    )}
    {error && <span role="alert" className="text-xs text-[#c53030] mt-1 block">{error}</span>}
  </div>
);

// ─── Info Card ────────────────────────────────────────────────────
const InfoCard = ({ icon, title, lines }) => (
  <div className="flex items-start gap-4 bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
    <div className="shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-[#115B9D] to-[#116DAE] flex items-center justify-center text-white">
      {icon}
    </div>
    <div>
      <p className="text-sm font-semibold text-gray-800 mb-1">{title}</p>
      {lines.map((line, i) => (
        <p key={i} className="text-sm text-gray-500">{line}</p>
      ))}
    </div>
  </div>
);

// ─── Contact Form (shared logic + UI) ────────────────────────────
/**
 * ContactForm
 *
 * Reusable form component used in both inline and full-page modes.
 *
 * Props:
 *  - inline {boolean} — when true, renders bare form fields only (no card
 *                       wrapper or section label). Useful for embedding inside
 *                       another card, modal, or layout.
 */
export const ContactForm = ({ inline = false }) => {
  const [form, setForm] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [dialCode, setDialCode] = useState("1");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handlePhoneChange = (fullNumber, countryData) => {
    setDialCode(countryData?.dialCode || "");
    setForm((prev) => ({ ...prev, mobileNo: fullNumber }));
    if (formErrors.mobileNo) setFormErrors((prev) => ({ ...prev, mobileNo: "" }));
  };

  const validate = () => {
    const errors = {};

    if (!form.fullName.trim()) {
      errors.fullName = "Full name is required";
    } else if (form.fullName.trim().length < 2) {
      errors.fullName = "Name must be at least 2 characters";
    }

    if (!form.email.trim()) {
      errors.email = "Email address is required";
    } else if (!isValidEmail(form.email)) {
      errors.email = "Please enter a valid email address";
    }

    if (!form.mobileNo || form.mobileNo.replace(/\D/g, "") === dialCode) {
      errors.mobileNo = "Phone number is required";
    } else if (!isValidPhone(form.mobileNo)) {
      errors.mobileNo = "Please enter a valid phone number (7–15 digits)";
    }

    if (!form.message.trim()) {
      errors.message = "Message is required";
    } else if (form.message.trim().length < 10) {
      errors.message = "Message must be at least 10 characters";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    loader(true);
    const payload = {
      fullName: form.fullName.trim(),
      email: form.email.trim(),
      mobileNo: form.mobileNo,
      message: form.message.trim(),
    };

    await ApiClient.post("contactUs/add", payload)
      .then((res) => {
        if (res?.success) {
          swal({
            text: "Your message has been sent successfully. We'll get back to you soon!",
            icon: "success",
            className: "smallModal",
          });
          setForm(emptyForm);
          setFormErrors({});
          setDialCode("1");
        }
      })
      .catch(console.error);

    loader(false);
  };

  // ── Shared form fields ──
  const formFields = (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <FormInput
          label="Full Name"
          name="fullName"
          required
          value={form.fullName}
          onChange={handleChange}
          error={formErrors.fullName}
          placeholder="e.g. John Doe"
        />
        <FormInput
          label="Email Address"
          name="email"
          type="email"
          required
          value={form.email}
          onChange={handleChange}
          error={formErrors.email}
          placeholder="e.g. john@example.com"
        />
      </div>

      <div>
        <label className="block text-sm text-gray-700 mb-1">
          Phone Number <span className="text-red-400">*</span>
        </label>
        <PhoneInput
          country="in"
          value={form.mobileNo}
          onChange={handlePhoneChange}
          enableSearch
          searchPlaceholder="Search country..."
          inputStyle={{
            width: "100%",
            height: "44px",
            fontSize: "14px",
            color: "#1f2937",
            borderRadius: "12px",
            border: formErrors.mobileNo ? "1px solid #f87171" : "1px solid #e5e7eb",
            paddingLeft: "48px",
            outline: "none",
            transition: "border-color 0.15s, box-shadow 0.15s",
          }}
          buttonStyle={{
            borderRadius: "12px 0 0 12px",
            border: formErrors.mobileNo ? "1px solid #f87171" : "1px solid #e5e7eb",
            borderRight: "none",
            background: "#f9fafb",
          }}
          dropdownStyle={{
            borderRadius: "12px",
            boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
            fontSize: "13px",
          }}
          containerStyle={{ width: "100%" }}
          inputProps={{ name: "mobileNo", required: true }}
        />
        {formErrors.mobileNo && (
          <span role="alert" className="text-xs text-[#c53030] mt-1 block">{formErrors.mobileNo}</span>
        )}
      </div>

      <FormInput
        label="Message"
        name="message"
        textarea
        required
        value={form.message}
        onChange={handleChange}
        error={formErrors.message}
        placeholder="e.g. I have a question about your soda dispenser products..."
      />

      <div className="flex justify-end pt-1">
        <button
          type="button"
          onClick={handleSubmit}
          className="flex gap-2 items-center bg-gradient-to-br from-[#115B9D] to-[#116DAE] text-white rounded-full text-sm hover:scale-[1.04] duration-300 transition px-6 py-2.5"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 2 11 13" />
            <path d="M22 2 15 22 11 13 2 9l20-7z" />
          </svg>
          Send Message
        </button>
      </div>
    </div>
  );

  // ── Inline mode: bare fields only, no wrapper ──
  if (inline) return formFields;

  // ── Page mode: fields inside card with section label ──
  return (
    <div className="lg:col-span-2 bg-white rounded-2xl shadow-md border border-gray-100 py-8 px-5 md:px-8">
      <p className="text-sm font-semibold text-[#115B9D] uppercase tracking-widest mb-5">
        Send Us a Message
      </p>
      {formFields}
    </div>
  );
};

// ─── Page ─────────────────────────────────────────────────────────
/**
 * ContactUs
 *
 * Props:
 *  - inline {boolean} — when true, renders ONLY the bare form fields with no
 *                       Header, Footer, hero banner, info cards, or card wrapper.
 *                       Useful for embedding the form inside another page/modal.
 *
 * Usage — full page:
 *   <ContactUs />
 *
 * Usage — form only (embedded):
 *   <ContactUs inline />
 */
export default function ContactUs({ inline = false }) {
  // ── Inline: just the raw form fields ──
  if (inline) return <ContactForm inline />;

  // ── Full page ──
  return (
    <div className="">
      <Header />
      <div className="min-h-[calc(100vh_-_75px)] md:min-h-[calc(100vh_-_95px)] mt-[75px] lg:mt-[95px]">
        {/* Hero */}
        <div className="bg-gradient-to-br from-[#115B9D] to-[#116DAE]">
          <div className="container mx-auto px-5 py-10 text-white">
            <h1 className="text-[28px] sm:text-[36px] font-bold leading-tight">Contact Us</h1>
            <p className="mt-2 text-blue-100 text-sm sm:text-base max-w-lg">
              Have questions or need support? We'd love to hear from you. Fill out the form and our team will get back to you shortly.
            </p>
          </div>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-5 py-10 flex-1">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

            {/* Info Cards */}
            <div className="space-y-4">
              <h2 className="text-[15px] font-semibold text-gray-700 mb-4">Get In Touch</h2>

              <InfoCard
                icon={
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.27h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 8a16 16 0 0 0 6 6l.92-.92a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 21.73 16a2 2 0 0 1 .27.92z" />
                  </svg>
                }
                title="Phone"
                lines={["+1 (800) 123-4567", "Mon–Fri, 9am–6pm EST"]}
              />

              <InfoCard
                icon={
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect width="20" height="16" x="2" y="4" rx="2" />
                    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                  </svg>
                }
                title="Email"
                lines={["support@example.com", "We reply within 24 hours"]}
              />

              <InfoCard
                icon={
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                }
                title="Address"
                lines={["123 Business Avenue", "New York, NY 10001, USA"]}
              />

              <InfoCard
                icon={
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                }
                title="Business Hours"
                lines={["Monday – Friday: 9am – 6pm", "Saturday: 10am – 3pm"]}
              />
            </div>

            {/* Form Card */}
            <ContactForm />

          </div>
        </div>
      </div>

      {methodModel.TokenChecker() && <Footer />}
    </div>
  );
}